import React, { useContext } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import GlobalContext from "../../context/GlobalContext";
import { fetchAttendanceLists } from "../../api/WorkspaceApi";
import { getAttendanceAnalytics } from "../../api/AnalyticsApi";
import AttendanceExcelExporter from "../../helper/AttendanceExcelExporter";
import swal from "../../services/swalConfig";

import { tr } from "../../i18n";
const ModalExportAttendance = ({ show, onClose }) => {
  const { user, setLoading } = useContext(GlobalContext);
  const companyId = user?.workspace?.company_id;

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      date_from: "",
      date_to: "",
    },
  });

  const handleModalClose = () => {
    reset();
    onClose();
  };

  const submitHandler = async (formData) => {
    if (!companyId) return;

    setLoading(true);

    try {
      const response = await fetchAttendanceLists({
        companyId,
        page: 1,
        perPage: 0,
        dateFrom: formData.date_from,
        dateTo: formData.date_to,
      });

      if (response.status !== 200) {
        swal.fire({
          title: tr("common:errors.oops"),
          text: response.message || tr("attendance:failed_to_fetch_attendance_data"),
          icon: "error",
        });
        return;
      }

      if (!response.data || response.data.length === 0) {
        swal.fire({
          title: tr("attendance:no_data"),
          text: tr("attendance:there_is_no_attendance_data_for"),
          icon: "warning",
        });
        return;
      }

      // Summary sheet (existing analytics endpoint) requires an explicit range,
      // so derive one from the fetched rows when the user left the fields empty.
      const attendanceDates = response.data.map((item) => item.attendance_date);
      const effectiveDateFrom =
        formData.date_from || attendanceDates.reduce((min, d) => (d < min ? d : min), attendanceDates[0]);
      const effectiveDateTo =
        formData.date_to || attendanceDates.reduce((max, d) => (d > max ? d : max), attendanceDates[0]);

      let analyticsData = null;
      const analyticsResponse = await getAttendanceAnalytics({
        companyId,
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
      });

      if (analyticsResponse.status === 200) {
        analyticsData = analyticsResponse.data;
      }

      const result = AttendanceExcelExporter.exportToExcel(
        response.data,
        {
          date_from: formData.date_from,
          date_to: formData.date_to,
        },
        analyticsData
      );

      if (result.success) {
        swal.fire({
          title: tr("shared:success_2"),
          text: tr("attendance:attendance_exported_filename", { filename: result.filename }),
          icon: "success",
        });
        handleModalClose();
      } else {
        swal.fire({
          title: tr("common:errors.oops"),
          text: result.message || tr("attendance:export_failed"),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("common:errors.oops"),
        text: tr("common:errors.generic"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="md" centered>
      <Form onSubmit={handleSubmit(submitHandler)}>
        <Modal.Header closeButton>
          <Modal.Title>{tr("attendance:export_attendance")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            {tr("attendance:choose_a_date_range_to_export")}
          </p>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("attendance:date_from")}</Form.Label>
                <Form.Control
                  type="date"
                  {...register("date_from")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("attendance:date_to")}</Form.Label>
                <Form.Control type="date" {...register("date_to")} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            {tr("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm"
          >
            <FontAwesomeIcon icon={faFileExport} />
            {tr("attendance:export_to_excel")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalExportAttendance;
